// bulkSnapshotRunner.jsx
// Fixes:
// 1) Authorization header uses RAW JWT (no "Bearer " prefix) to match backend verifyToken middleware.
// 2) Properly treats vendor "error" payloads as FAIL (previously they were counted as OK when Promise fulfilled).
// Provides both a runner function (for programmatic use) and an optional React UI component.

import React, { useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";

import { fetchCurrentExtract } from "../../krs/krsApi";
import {
  fetchPrzeswietlReport,
  fetchRiskReport,
  fetchRelationsReport,
} from "../tdClient";

// -----------------------
// Token helpers
// -----------------------
export function rawAuthToken(token) {
  if (!token) return "";
  const t = String(token).trim();
  return t.toLowerCase().startsWith("bearer ") ? t.slice(7).trim() : t;
}

// Prefer adminToken in admin panel; fallback to token.
export function getAnyTokenFromStorage() {
  const adminToken = localStorage.getItem("adminToken");
  const token = localStorage.getItem("token");
  return rawAuthToken(adminToken || token || "");
}

// ---------- helpers copied from your report ----------
function decodeJwtPayload(t) {
  try {
    return JSON.parse(atob(String(t).split(".")[1]));
  } catch {
    return {};
  }
}

function pickRelationsForSchema(rel) {
  if (!rel || typeof rel !== "object") return null;
  const src = rel.json && typeof rel.json === "object" ? rel.json : rel;
  const out = {};
  if (src.company) out.company = src.company;
  if (src.relations) out.relations = src.relations;
  if (src.graph) out.graph = src.graph;
  return Object.keys(out).length ? out : null;
}

function isValidKrsNumber(krsNumber) {
  const s = String(krsNumber ?? "").replace(/\D/g, "");
  return /^\d{10}$/.test(s);
}

function buildSnapshotPayload({
  nip,
  REL_DEPTH,
  apiResult,
  riskResult,
  relationsResult,
  krsOdpisRaw,
  krsEmails,
  krs,
  knf,
  publicDebt,
  ure,
  white,
  euGrants,
  partners,
  reporter,
}) {
  return {
    meta: {
      generatedAt: new Date().toISOString(),
      lastUpdate: new Date().toISOString(),
      reportGeneratedBy: reporter ? [String(reporter)] : [],
      nip: String(nip || "").replace(/\D/g, ""),
      relDepth: REL_DEPTH,
    },
    sources: {
      przeszwietl: { result: apiResult || null, error: null },
      risk: { result: riskResult || null, error: null },
      relations: { result: pickRelationsForSchema(relationsResult), error: null },
      krsExtract: { raw: krsOdpisRaw || null },
    },
    derived: {
      emailsFromKrs: krsEmails || [],
      krsResolved: krs || null,
      knf: knf || [],
      publicDebt: publicDebt || [],
      ure: ure || [],
      whiteList: white || null,
      euGrants: euGrants || [],
      partners: partners || [],
    },
  };
}

// -----------------------
// Vendor error detection (NEW)
// -----------------------
function safeJsonParse(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function isObj(x) {
  return x && typeof x === "object" && !Array.isArray(x);
}

/**
 * Detects "error" responses that still come with HTTP 200.
 * Supports shapes seen in your logs like:
 *  - { error: "Parameter NIP jest niepoprawny", td: { status:"error", ... } }
 *  - { td: { status:"error", ... }, error_message: "..." }
 *  - { result: { error: "..."} }
 *  - stringified JSON payload
 */
function hasVendorError(x) {
  if (!x) return false;

  if (typeof x === "string") {
    const parsed = safeJsonParse(x);
    return parsed ? hasVendorError(parsed) : false;
  }

  if (!isObj(x)) return false;

  // direct flags
  if (x.status === "error") return true;
  if (x.error) return true;
  if (x.error_message) return true;

  // nested 'td' (transparentdata style)
  if (isObj(x.td) && x.td.status === "error") return true;
  if (isObj(x.td) && (x.td.error || x.td.error_message)) return true;

  // nested 'result'
  if (isObj(x.result)) {
    if (x.result.status === "error") return true;
    if (x.result.error || x.result.error_message) return true;
    if (isObj(x.result.td) && x.result.td.status === "error") return true;
  }

  // sometimes errors are in first array element etc.
  if (Array.isArray(x.data) && x.data.length) {
    for (const el of x.data) {
      if (hasVendorError(el)) return true;
    }
  }

  return false;
}

function extractVendorErrorMessage(x) {
  if (!x) return null;

  if (typeof x === "string") {
    const parsed = safeJsonParse(x);
    if (parsed) return extractVendorErrorMessage(parsed);
    return x; // raw string
  }

  if (!isObj(x)) return null;

  return (
    x.error ||
    x.error_message ||
    x?.td?.error ||
    x?.td?.error_message ||
    x?.result?.error ||
    x?.result?.error_message ||
    x?.result?.td?.error ||
    x?.result?.td?.error_message ||
    null
  );
}

async function postCompanySnapshot({ token, payload, signal }) {
  const res = await fetch("https://server229256.nazwa.pl/api/company-snapshots", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: rawAuthToken(token),
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      msg += ` - ${JSON.stringify(await res.json())}`;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

async function getLatestSnapshot({ token, nip, signal }) {
  const res = await fetch(
    `https://server229256.nazwa.pl/api/company-snapshots/${nip}/latest`,
    { headers: { Authorization: rawAuthToken(token) }, signal }
  );

  if (res.status === 404) return null;

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      msg += ` - ${JSON.stringify(await res.json())}`;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

function get(obj, path, dflt = undefined) {
  return path
    .split(".")
    .reduce((o, k) => (o && o[k] !== undefined ? o[k] : dflt), obj);
}

function isStale(snapshot, maxDays = 15) {
  const ts = get(snapshot, "meta.lastUpdate") || get(snapshot, "meta.generatedAt");
  if (!ts) return true;
  const ageMs = Date.now() - new Date(ts).getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  return ageDays > maxDays;
}

function extractEmailsFromD1(d1) {
  const node = get(d1, "siedzibaIAdres.adresPocztyElektronicznej");
  const asArr = Array.isArray(node) ? node : node ? [node] : [];
  return [
    ...new Set(
      asArr
        .flatMap((x) =>
          typeof x === "string"
            ? [x]
            : [x?.adresPocztyElektronicznej, x?.email, x?.value].filter(Boolean)
        )
        .map((s) => String(s).trim())
    ),
  ].filter(Boolean);
}

async function fetchEmailsViaKrsClient(krsNumber) {
  try {
    const raw = await fetchCurrentExtract(krsNumber);
    const d1 = get(raw, "odpis.dane.dzial1", null);
    const emails = extractEmailsFromD1(d1);
    return { raw, emails };
  } catch {
    return { raw: null, emails: [] };
  }
}

// ---------- MAIN: generate snapshot for one NIP ----------
export async function generateSnapshotForNip({ nip, token, relDepth = 2, signal }) {
  const clean = String(nip || "").replace(/\D/g, "");
  const authToken = rawAuthToken(token);

  if (!/^\d{10}$/.test(clean)) {
    return {
      ok: false,
      nip: clean,
      status: "invalid_nip",
      message: "Invalid NIP (10 digits required)",
    };
  }

  // jwtDecode must see raw JWT (not "Bearer ...")
  const jwtData = (() => {
    try {
      return jwtDecode(authToken);
    } catch {
      return decodeJwtPayload(authToken);
    }
  })();

  const reporter = jwtData?.Company || jwtData?.company || "unknown";

  // 1) cache check (requires Authorization header RAW JWT)
  const cached = await getLatestSnapshot({ token: authToken, nip: clean, signal });
  if (cached && !isStale(cached, 15)) {
    return { ok: true, nip: clean, status: "already_fresh" };
  }

  // 2) vendor APIs (they should also use RAW JWT in Authorization)
  const REL_DEPTH = relDepth;

  const [prz, risk, rel] = await Promise.allSettled([
    fetchPrzeswietlReport({ nip: clean, token: authToken }),
    fetchRiskReport({ nip: clean, token: authToken }),
    fetchRelationsReport
      ? fetchRelationsReport({ nip: clean, depth: REL_DEPTH, token: authToken })
      : Promise.resolve({ result: null }),
  ]);

  const przFirst =
    prz.status === "fulfilled"
      ? prz.value?.result || prz.value?.data?.[0]?.result || null
      : null;

  const riskFirst =
    risk.status === "fulfilled"
      ? risk.value?.result || risk.value?.data?.[0]?.result || null
      : null;

  const relationsRaw = rel.status === "fulfilled" ? rel.value?.result || null : null;

  // -----------------------
  // NEW: "fulfilled" != success if payload contains {error:...}
  // -----------------------
  const przOk =
    prz.status === "fulfilled" && !hasVendorError(prz.value?.result || przFirst || prz.value);

  const riskOk =
    risk.status === "fulfilled" && !hasVendorError(risk.value?.result || riskFirst || risk.value);

  const relOk = fetchRelationsReport
    ? rel.status === "fulfilled" && !hasVendorError(rel.value?.result || relationsRaw || rel.value)
    : false;

  // If NOTHING truly succeeded -> fail
  if (!przOk && !riskOk && !relOk) {
    const msg =
      (prz.status === "fulfilled"
        ? extractVendorErrorMessage(prz.value?.result || przFirst || prz.value)
        : prz.reason?.message) ||
      (risk.status === "fulfilled"
        ? extractVendorErrorMessage(risk.value?.result || riskFirst || risk.value)
        : risk.reason?.message) ||
      (rel.status === "fulfilled"
        ? extractVendorErrorMessage(rel.value?.result || relationsRaw || rel.value)
        : rel.reason?.message) ||
      "No report data";

    // Optional: classify invalid vendor NIP as SKIP instead of FAIL
    if (/nip.*niepoprawny/i.test(String(msg || ""))) {
      return {
        ok: false,
        nip: clean,
        status: "skip_invalid_vendor_nip",
        message: msg,
      };
    }

    return {
      ok: false,
      nip: clean,
      status: "vendor_failed",
      message: msg || "Failed vendor reports",
    };
  }

  // 3) KRS emails (optional)
  let krsOdpisRaw = null;
  let krsEmails = [];

  const krsNumber =
    przFirst?.krs?.krs ||
    przFirst?.krs?.krs_number ||
    przFirst?.krs?.numerKRS ||
    przFirst?.krs;

  if (isValidKrsNumber(krsNumber)) {
    const { raw, emails } = await fetchEmailsViaKrsClient(krsNumber);
    krsOdpisRaw = raw;
    krsEmails = emails;
  }

  // 4) build payload & post snapshot (Authorization RAW JWT)
  const krsObj = przFirst?.krs && typeof przFirst.krs === "object" ? { ...przFirst.krs } : null;

  const knf = Array.isArray(przFirst?.knf) ? przFirst.knf : [];
  const publicDebt = Array.isArray(przFirst?.public_debt) ? przFirst.public_debt : [];
  const ure = Array.isArray(przFirst?.ure) ? przFirst.ure : [];
  const white = przFirst?.white_list || null;

  const euGrants =
    (Array.isArray(przFirst?.eu_grants) && przFirst.eu_grants.length
      ? przFirst.eu_grants
      : Array.isArray(white?.eu_grants)
      ? white.eu_grants
      : []) || [];

  const partnersTop = Array.isArray(przFirst?.partners) ? przFirst.partners : [];
  const partnersWhite = Array.isArray(white?.relations?.partners) ? white.relations.partners : [];
  const partners = [...partnersTop, ...partnersWhite];

  const payload = buildSnapshotPayload({
    nip: clean,
    REL_DEPTH,
    apiResult: przFirst || null,
    riskResult: riskFirst || null,
    relationsResult: relationsRaw,
    krsOdpisRaw,
    krsEmails,
    krs: krsObj,
    knf,
    publicDebt,
    ure,
    white,
    euGrants,
    partners,
    reporter,
  });

  await postCompanySnapshot({ token: authToken, payload, signal });

  return { ok: true, nip: clean, status: cached ? "refreshed" : "created" };
}

// -----------------------
// Bulk runner (concurrency-limited)
// -----------------------
function normalizeNips(input) {
  const arr = Array.isArray(input) ? input : [];
  const cleaned = arr
    .map((x) => String(x ?? "").replace(/\D/g, ""))
    .filter((x) => /^\d{10}$/.test(x));
  // de-dupe while preserving order
  const seen = new Set();
  const out = [];
  for (const n of cleaned) {
    if (!seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

async function runPool({ items, concurrency, worker, signal }) {
  const results = new Array(items.length);
  let idx = 0;

  const runners = Array.from({ length: Math.max(1, concurrency) }, async () => {
    while (true) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const i = idx++;
      if (i >= items.length) break;
      try {
        results[i] = await worker(items[i], i);
      } catch (e) {
        results[i] = { ok: false, error: e?.message || String(e), item: items[i] };
      }
    }
  });

  await Promise.all(runners);
  return results;
}

/**
 * Run snapshots generation for an array of NIPs.
 *
 * @param {Object} opts
 * @param {string[]} opts.nips
 * @param {string} opts.token
 * @param {number} [opts.relDepth=2]
 * @param {number} [opts.concurrency=2]
 * @param {AbortSignal} [opts.signal]
 * @param {(event: {type:string, nip?:string, index?:number, result?:any})=>void} [opts.onEvent]
 */
export async function runBulkSnapshots({
  nips,
  token,
  relDepth = 2,
  concurrency = 2,
  signal,
  onEvent,
} = {}) {
  const list = normalizeNips(nips);
  const authToken = rawAuthToken(token);

  if (!authToken) {
    throw new Error("Missing token (Authorization). Please login again.");
  }

  onEvent?.({ type: "start", total: list.length });

  const results = await runPool({
    items: list,
    concurrency,
    signal,
    worker: async (nip, index) => {
      onEvent?.({ type: "item_start", nip, index });
      const result = await generateSnapshotForNip({
        nip,
        token: authToken,
        relDepth,
        signal,
      });
      onEvent?.({ type: "item_done", nip, index, result });
      return result;
    },
  });

  const summary = results.reduce(
    (acc, r) => {
      acc.total++;
      if (r?.ok) acc.ok++;
      else acc.fail++;
      acc.byStatus[r?.status || "unknown"] = (acc.byStatus[r?.status || "unknown"] || 0) + 1;
      return acc;
    },
    { total: 0, ok: 0, fail: 0, byStatus: {} }
  );

  onEvent?.({ type: "done", summary, results });
  return { summary, results };
}

// -----------------------
// Optional React component UI
// -----------------------
export default function BulkSnapshotRunner({
  nips = [],
  relDepthDefault = 2,
  concurrencyDefault = 2,
  token: tokenProp,
  onFinished,
}) {
  const token = tokenProp || getAnyTokenFromStorage();

  const [relDepth, setRelDepth] = useState(relDepthDefault);
  const [concurrency, setConcurrency] = useState(concurrencyDefault);

  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [summary, setSummary] = useState(null);

  const abortRef = useRef(null);

  const normalized = useMemo(() => normalizeNips(nips), [nips]);

  const pushLog = (line) => setLog((prev) => [...prev, line]);

  const onEvent = (e) => {
    if (e.type === "start") pushLog(`Starting bulk run: ${e.total} NIPs`);
    if (e.type === "item_start") pushLog(`[${e.index + 1}] ${e.nip} ...`);
    if (e.type === "item_done") {
      const r = e.result;
      pushLog(
        `[${e.index + 1}] ${e.nip} -> ${r?.ok ? "OK" : "FAIL"} (${r?.status || "unknown"})${
          r?.message ? ` - ${r.message}` : ""
        }`
      );
    }
    if (e.type === "done") {
      pushLog(`Done. OK: ${e.summary.ok} | FAIL: ${e.summary.fail}`);
      setSummary(e.summary);
      onFinished?.(e);
    }
  };

  const start = async () => {
    setRunning(true);
    setLog([]);
    setSummary(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await runBulkSnapshots({
        nips: normalized,
        token,
        relDepth,
        concurrency,
        signal: controller.signal,
        onEvent,
      });
    } catch (e) {
      if (e?.name === "AbortError") pushLog("Aborted by user.");
      else pushLog(`Runner error: ${e?.message || String(e)}`);
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const stop = () => {
    abortRef.current?.abort();
  };

  return (
    <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 14, background: "#fff" }}>
      <div style={{ fontWeight: 900, marginBottom: 6 }}>Bulk Snapshot Runner</div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontWeight: 800 }}>relDepth</span>
          <input
            type="number"
            min={1}
            max={3}
            value={relDepth}
            onChange={(e) => setRelDepth(Number(e.target.value))}
            disabled={running}
            style={{ width: 70, padding: 6, borderRadius: 8, border: "1px solid #ddd" }}
          />
        </label>

        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontWeight: 800 }}>concurrency</span>
          <input
            type="number"
            min={1}
            max={8}
            value={concurrency}
            onChange={(e) => setConcurrency(Number(e.target.value))}
            disabled={running}
            style={{ width: 70, padding: 6, borderRadius: 8, border: "1px solid #ddd" }}
          />
        </label>

        <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={start}
            disabled={running || normalized.length === 0}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "none",
              cursor: running ? "not-allowed" : "pointer",
              background: "#009fb7",
              color: "#fff",
              fontWeight: 900,
            }}
          >
            {running ? "Running..." : `Run (${normalized.length})`}
          </button>

          <button
            type="button"
            onClick={stop}
            disabled={!running}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #ddd",
              cursor: !running ? "not-allowed" : "pointer",
              background: "#fff",
              color: "#333",
              fontWeight: 900,
            }}
          >
            Stop
          </button>
        </div>
      </div>

      {summary && (
        <div style={{ marginTop: 12, fontSize: 13, fontWeight: 800, opacity: 0.9 }}>
          Summary: OK {summary.ok} / FAIL {summary.fail} / TOTAL {summary.total}
        </div>
      )}

      <pre
        style={{
          marginTop: 12,
          background: "#0b1220",
          color: "#e5e7eb",
          padding: 12,
          borderRadius: 12,
          maxHeight: 320,
          overflow: "auto",
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        {log.join("\n")}
      </pre>
    </div>
  );
}
